import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Global auth synchronization hook.
 * Listens to Supabase auth state changes and invalidates ALL relevant
 * React Query caches so that components always use fresh data.
 *
 * Important: Supabase auth callbacks should stay synchronous. Cache work is
 * deferred to the next tick to avoid lock-related auth deadlocks.
 */
export function useAuthSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[AuthSync] Auth event:', event, session ? 'has session' : 'no session');

        // Meta Pixel: SignUp-Event nur EINMAL pro Session bei tatsächlicher
        // Neu-Registrierung feuern. Kein Event bei normalen Logins oder
        // TOKEN_REFRESHED (verhindert Mehrfach-Feuerung).
        if (event === 'SIGNED_IN' && session?.user) {
          const createdAt = new Date(session.user.created_at).getTime();
          const now = Date.now();
          const isNewUser = now - createdAt < 60_000; // erstellt in den letzten 60s

          const SIGNUP_FIRED_KEY = 'meta_pixel_signup_fired';
          const alreadyFired =
            typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem(SIGNUP_FIRED_KEY) === '1';

          if (
            isNewUser &&
            !alreadyFired &&
            typeof window !== 'undefined' &&
            (window as any).fbq
          ) {
            (window as any).fbq('track', 'SignUp', {
              content_name: 'Account Registrierung',
              content_category: 'signup',
            });
            sessionStorage.setItem(SIGNUP_FIRED_KEY, '1');
          }
        }

        if (
          event === 'SIGNED_IN' ||
          event === 'SIGNED_OUT' ||
          event === 'TOKEN_REFRESHED' ||
          event === 'USER_UPDATED'
        ) {
          window.setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['current-user'] });
            queryClient.invalidateQueries({ queryKey: ['global-access'] });
            queryClient.invalidateQueries({ queryKey: ['user-profile'] });
            queryClient.invalidateQueries({ queryKey: ['subscription'] });

            if (event === 'SIGNED_OUT') {
              queryClient.clear();
            }
          }, 0);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient]);
}
